import { useState, useRef } from 'react'
import { X, ChevronRight } from 'lucide-react'
import { supabase } from '../../services/supabase'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { useAdvisors } from '../../hooks/useAdvisors'
import { useTaskSections } from '../../hooks/useTaskSections'
import { AdvisorAvatar } from '../Common/AdvisorAvatar'

const PRIORITIES = ['low', 'medium', 'high']

export function CreateTaskModal({
  isOpen, onClose, onCreated, isDarkMode,
  parentTaskId = null, parentTaskTitle = null,
  initialClientId = null, initialClientName = null,
  initialPolicyId = null, initialPolicyNumber = null,
}) {
  const { advisor: currentAdvisor } = useCurrentAdvisor()
  const { advisors } = useAdvisors()
  const { sections } = useTaskSections()

  const [form, setForm] = useState({
    title: '',
    section: '',
    advisor_id: '',
    due_date: '',
    priority: 'medium',
    description: '',
    client_name: initialClientName || '',
    policy_number: initialPolicyNumber || '',
    crm_client_id: initialClientId || null,
    policy_id: initialPolicyId || null,
  })

  // Co-advisor state
  const [coAdvisors, setCoAdvisors] = useState([])   // [{id, full_name, avatar_url}]
  const [coSearch, setCoSearch] = useState('')
  const [showCoDropdown, setShowCoDropdown] = useState(false)
  const coSearchRef = useRef(null)

  // Client search state
  const [clientSearch, setClientSearch] = useState('')
  const [clientResults, setClientResults] = useState([])
  const [clientSearchLoading, setClientSearchLoading] = useState(false)
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(
    initialClientId ? { id: initialClientId, full_name: initialClientName ?? '', customer_code: null } : null
  )
  const clientSearchRef = useRef(null)
  const clientSearchTimeoutRef = useRef(null)

  // Policy picker state
  const [clientPolicies, setClientPolicies] = useState([])
  const [clientPoliciesLoading, setClientPoliciesLoading] = useState(false)
  const [_selectedPolicy, setSelectedPolicy] = useState(
    initialPolicyId ? { id: initialPolicyId, policy_number: initialPolicyNumber ?? '' } : null
  )

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  if (!isOpen) return null

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

  // Debounced client search
  const handleClientSearch = async (query) => {
    setClientSearch(query)

    if (clientSearchTimeoutRef.current) {
      clearTimeout(clientSearchTimeoutRef.current)
    }

    if (!query.trim()) {
      setClientResults([])
      setShowClientDropdown(false)
      return
    }

    setClientSearchLoading(true)
    clientSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('clients')
          .select('id, full_name, surname, customer_code')
          .or(`full_name.ilike.%${query}%, surname.ilike.%${query}%, customer_code.ilike.%${query}%`)
          .limit(8)

        setClientResults(data || [])
        setShowClientDropdown(true)
      } catch (err) {
        console.error('Client search error:', err)
        setClientResults([])
      } finally {
        setClientSearchLoading(false)
      }
    }, 300)
  }

  const loadClientPolicies = async (clientId) => {
    setClientPoliciesLoading(true)
    try {
      // First, get client's customer_code for matching
      const { data: clientData } = await supabase
        .from('clients')
        .select('customer_code')
        .eq('id', clientId)
        .maybeSingle()

      // Query policies by client_id OR customer_code (match either way)
      let query = supabase
        .from('policies')
        .select('id, policy_number, product, category')

      if (clientData?.customer_code) {
        // Try both client_id and customer_code
        query = query.or(`client_id.eq.${clientId},customer_code.eq.${clientData.customer_code}`)
      } else {
        // Fallback to just client_id
        query = query.eq('client_id', clientId)
      }

      const { data } = await query.eq('active', true).order('policy_number')
      setClientPolicies(data || [])
    } catch (err) {
      console.error('Policy loading error:', err)
      setClientPolicies([])
    } finally {
      setClientPoliciesLoading(false)
    }
  }

  const selectClient = (client) => {
    setSelectedClient(client)
    set('client_name', client.full_name)
    set('crm_client_id', client.id)
    setClientSearch('')
    setClientResults([])
    setShowClientDropdown(false)
    loadClientPolicies(client.id)
  }

  const clearClient = () => {
    setSelectedClient(null)
    set('client_name', '')
    set('crm_client_id', null)
    set('policy_number', '')
    set('policy_id', null)
    setClientSearch('')
    setClientResults([])
    setClientPolicies([])
    setSelectedPolicy(null)
  }

  const leadAdvisorId = form.advisor_id || currentAdvisor?.id

  // Advisors available to add as co-advisors
  const coDropdownAdvisors = advisors.filter(a =>
    a.id !== leadAdvisorId &&
    !coAdvisors.some(c => c.id === a.id) &&
    a.full_name.toLowerCase().includes(coSearch.toLowerCase())
  ).slice(0, 6)

  const addCoAdvisor = (a) => {
    setCoAdvisors(prev => [...prev, a])
    setCoSearch('')
    setShowCoDropdown(false)
    coSearchRef.current?.focus()
  }

  const removeCoAdvisor = (id) => setCoAdvisors(prev => prev.filter(a => a.id !== id))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) { setError('Title is required'); return }
    setSaving(true)
    setError(null)

    const payload = {
      title:          form.title.trim(),
      section:        form.section,
      advisor_id:     leadAdvisorId,
      due_date:       form.due_date || null,
      priority:       form.priority,
      description:    form.description.trim() || null,
      client_name:    form.client_name.trim() || null,
      policy_number:  form.policy_number.trim() || null,
      crm_client_id:  form.crm_client_id || null,
      policy_id:  form.policy_id || null,
      task_type:      'manual',
      status:         'open',
      created_by:     currentAdvisor?.id ?? null,
      parent_task_id: parentTaskId || null,
    }

    const { data: taskData, error: err } = await supabase
      .from('sfp_tasks').insert([payload]).select('id').single()

    if (err) { setError(err.message); setSaving(false); return }

    // Insert co-advisors into junction table
    if (coAdvisors.length > 0 && taskData?.id) {
      await supabase.from('sfp_task_advisors').insert(
        coAdvisors.map(a => ({
          task_id:    taskData.id,
          advisor_id: a.id,
          added_by:   currentAdvisor?.id ?? null,
        }))
      )
    }

    setSaving(false)
    setForm({ title: '', section: '', advisor_id: '', due_date: '', priority: 'medium', description: '', client_name: '', policy_number: '', crm_client_id: null, policy_id: null })
    setCoAdvisors([])
    setCoSearch('')
    setSelectedClient(null)
    setClientSearch('')
    setClientResults([])
    setClientPolicies([])
    setSelectedPolicy(null)
    onCreated?.()
    onClose()
  }

  const overlay = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4'
  const modal = `w-full max-w-lg rounded-xl shadow-2xl border ${
    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
  }`
  const label = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`
  const input = `w-full px-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-sfp-orange/40 transition ${
    isDarkMode
      ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-500'
      : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
  }`
  
  return (
    <div className={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={modal}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
          <h2 className={`text-base font-semibold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
            {parentTaskTitle ? 'Create Subtask' : 'Create Task'}
          </h2>
          <button onClick={onClose} className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
          {/* Subtask of banner */}
          {parentTaskTitle && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-700">
              <ChevronRight className="w-3 h-3 flex-shrink-0" />
              Subtask of: <strong>{parentTaskTitle}</strong>
            </div>
          )}

          {/* Title */}
          <div>
            <label className={label}>Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Task title"
              className={input}
              autoFocus
            />
          </div>

          {/* Section + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Section</label>
              <select value={form.section} onChange={(e) => set('section', e.target.value)} className={input}>
                <option value="">Select a section...</option>
                {sections.filter(s => s.active).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Priority</label>
              <select value={form.priority} onChange={(e) => set('priority', e.target.value)} className={input}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
          </div>

          {/* Lead Assignee + Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Lead Assignee</label>
              <select value={form.advisor_id} onChange={(e) => set('advisor_id', e.target.value)} className={input}>
                <option value="">Me ({currentAdvisor?.full_name ?? '...'})</option>
                {advisors.map((a) => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => set('due_date', e.target.value)}
                className={input}
              />
            </div>
          </div>

          {/* Additional Advisors (co-advisors) */}
          <div>
            <label className={label}>Additional Advisors</label>
            {/* Chips */}
            {coAdvisors.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {coAdvisors.map(a => (
                  <div key={a.id} className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-blue-50 border-blue-200 text-blue-800'
                  }`}>
                    <AdvisorAvatar avatarUrl={a.avatar_url ?? null} fullName={a.full_name} size="sm" />
                    <span className="font-medium">{a.full_name}</span>
                    <button
                      type="button"
                      onClick={() => removeCoAdvisor(a.id)}
                      className="ml-0.5 text-gray-400 hover:text-red-500 leading-none transition"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Search input */}
            <div className="relative">
              <input
                ref={coSearchRef}
                type="text"
                value={coSearch}
                onChange={e => { setCoSearch(e.target.value); setShowCoDropdown(true) }}
                onFocus={() => setShowCoDropdown(true)}
                onBlur={() => setTimeout(() => setShowCoDropdown(false), 150)}
                placeholder="Type to search and add advisors…"
                className={input}
              />
              {showCoDropdown && coSearch.length > 0 && coDropdownAdvisors.length > 0 && (
                <div className={`absolute top-full left-0 right-0 z-50 rounded-lg border shadow-lg mt-1 max-h-36 overflow-y-auto ${
                  isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                }`}>
                  {coDropdownAdvisors.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onMouseDown={() => addCoAdvisor(a)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition hover:bg-sfp-orange/10 ${
                        isDarkMode ? 'text-slate-200' : 'text-gray-700'
                      }`}
                    >
                      <AdvisorAvatar avatarUrl={a.avatar_url ?? null} fullName={a.full_name} size="sm" />
                      {a.full_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Client Search */}
          <div>
            <label className={label}>Client</label>
            {selectedClient ? (
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-blue-50 border-blue-200'
              }`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-blue-900'}`}>
                  {selectedClient.full_name} — {selectedClient.customer_code}
                </span>
                <button
                  type="button"
                  onClick={clearClient}
                  className={`text-gray-400 hover:text-red-500 transition`}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={clientSearchRef}
                  type="text"
                  value={clientSearch}
                  onChange={(e) => handleClientSearch(e.target.value)}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                  placeholder="Search by name or code..."
                  className={input}
                />
                {clientSearchLoading && (
                  <span className={`absolute right-3 top-2.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Searching...
                  </span>
                )}
                {showClientDropdown && clientResults.length > 0 && (
                  <div className={`absolute top-full left-0 right-0 z-50 rounded-lg border shadow-lg mt-1 max-h-36 overflow-y-auto ${
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                  }`}>
                    {clientResults.map(client => (
                      <button
                        key={client.id}
                        type="button"
                        onMouseDown={() => selectClient(client)}
                        className={`w-full text-left px-3 py-2 text-sm transition hover:bg-sfp-orange/10 border-b last:border-b-0 ${
                          isDarkMode ? 'text-slate-200 border-slate-700' : 'text-gray-700 border-gray-100'
                        }`}
                      >
                        <div className="font-medium">{client.full_name}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          {client.customer_code}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Policy Picker (appears after client selected) */}
          {selectedClient && (
            <div>
              <label className={label}>Policy (optional)</label>
              {clientPoliciesLoading ? (
                <div className={`w-full px-3 py-2 text-sm rounded-lg border ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-400' : 'bg-gray-50 border-gray-300 text-gray-600'
                }`}>
                  Loading policies...
                </div>
              ) : clientPolicies.length === 0 ? (
                <div className={`w-full px-3 py-2 text-sm rounded-lg border ${
                  isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-400' : 'bg-gray-50 border-gray-300 text-gray-600'
                }`}>
                  No active policies found for this client
                </div>
              ) : (
                <select
                  value={form.policy_id || ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      set('policy_id', null)
                      set('policy_number', '')
                      setSelectedPolicy(null)
                    } else {
                      const policy = clientPolicies.find(p => p.id === e.target.value)
                      if (policy) {
                        set('policy_id', policy.id)
                        set('policy_number', policy.policy_number)
                        setSelectedPolicy(policy)
                      }
                    }
                  }}
                  className={input}
                >
                  <option value="">None</option>
                  {clientPolicies.map(policy => (
                    <option key={policy.id} value={policy.id}>
                      {policy.policy_number} — {policy.product} ({policy.category})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Policy Number */}
          <div>
            <label className={label}>Policy Number</label>
            <input
              type="text"
              value={form.policy_number}
              onChange={(e) => set('policy_number', e.target.value)}
              placeholder="Optional"
              className={input}
            />
          </div>

          {/* Description */}
          <div>
            <label className={label}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Add details..."
              rows={3}
              className={`${input} resize-none`}
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                isDarkMode
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-sfp-orange text-white hover:bg-sfp-orange/90 transition-colors disabled:opacity-60"
            >
              {saving ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
